import { Fragment, useCallback, useEffect, useState } from "react";
import { BellRing, Loader2, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Rule = {
  id: string;
  rule_key: string;
  label: string;
  from_step: string;
  to_step: string;
  min_conversion_pct: number;
  min_sample: number;
  window_hours: number;
  cooldown_hours: number;
  escalate_after_repeats: number;
  detection_mode: "threshold" | "anomaly" | "either" | "both";
  anomaly_confidence: number;
  baseline_windows: number;
  min_relative_drop_pct: number;
  enabled: boolean;
  last_checked_at: string | null;
  last_alerted_at: string | null;
  last_conversion_pct: number | null;
};

const input =
  "w-20 border border-border bg-background px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-1 focus:ring-orange";

export function FunnelAlertRules() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("funnel_alert_rules")
      .select("*")
      .order("created_at", { ascending: true });
    if (error) setStatus(error.message);
    setRules((data ?? []) as unknown as Rule[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = useCallback(async (id: string, changes: Partial<Rule>) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...changes } : r)));
    const { error } = await supabase
      .from("funnel_alert_rules")
      .update(changes as never)
      .eq("id", id);
    setStatus(error ? error.message : "Saved");
  }, []);

  const runNow = useCallback(async () => {
    setRunning(true);
    setStatus(null);
    const { data, error } = await supabase.functions.invoke("funnel-alert-check", { body: { source: "manual" } });
    setRunning(false);
    if (error) {
      setStatus(`Check failed: ${error.message}`);
      return;
    }
    const results = (data as { results?: { alerted?: boolean }[] })?.results ?? [];
    const fired = results.filter((r) => r.alerted).length;
    setStatus(
      fired > 0
        ? `${fired} alert${fired === 1 ? "" : "s"} raised — see admin notifications.`
        : "All monitored steps are above threshold.",
    );
    void load();
  }, [load]);

  return (
    <section aria-labelledby="alerts-heading" className="mt-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 id="alerts-heading" className="font-display uppercase text-xl">
            Conversion alerts
          </h2>
          <p className="text-muted-foreground mt-2 text-sm max-w-2xl">
            An hourly background check compares each step's conversion against your threshold
            and raises an admin notification when it drops. Alerts only fire once the window
            has enough people, and each rule waits out its cooldown before repeating. Significant-drop detection compares the current window to the previous windows and only fires when the drop is statistically real, not just noise. If a rule keeps breaching during its cooldown, it escalates once as critical to admins plus the escalation role.
          </p>
        </div>
        <button
          type="button"
          onClick={runNow}
          disabled={running}
          className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-[0.16em] hover:bg-muted/50 transition disabled:opacity-60"
        >
          {running ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Run check now
        </button>
      </div>

      {status && (
        <p role="status" className="mt-3 text-sm text-muted-foreground">
          {status}
        </p>
      )}

      {loading ? (
        <p className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading rules…
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                {[
                  "Rule",
                  "Alert below %",
                  "Min people",
                  "Window (h)",
                  "Cooldown (h)",
                  "Latest",
                  "On",
                ].map((h) => (
                  <th key={h} className="px-3 py-2 font-semibold whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => {
                const breached =
                  r.last_conversion_pct != null &&
                  r.last_conversion_pct < Number(r.min_conversion_pct);
                return (
                  <Fragment key={r.id}>
                  <tr className="border-t border-border align-middle">
                    <td className="px-3 py-2">
                      <span className="font-semibold">{r.label}</span>
                      <span className="block text-xs text-muted-foreground">
                        {r.from_step} → {r.to_step}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        aria-label={`${r.label} threshold percent`}
                        className={input}
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={r.min_conversion_pct}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? { ...x, min_conversion_pct: Number(e.target.value) }
                                : x,
                            ),
                          )
                        }
                        onBlur={(e) =>
                          patch(r.id, { min_conversion_pct: Number(e.target.value) })
                        }
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        aria-label={`${r.label} minimum sample`}
                        className={input}
                        type="number"
                        min={1}
                        value={r.min_sample}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((x) =>
                              x.id === r.id ? { ...x, min_sample: Number(e.target.value) } : x,
                            ),
                          )
                        }
                        onBlur={(e) => patch(r.id, { min_sample: Number(e.target.value) })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        aria-label={`${r.label} window hours`}
                        className={input}
                        type="number"
                        min={1}
                        value={r.window_hours}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((x) =>
                              x.id === r.id ? { ...x, window_hours: Number(e.target.value) } : x,
                            ),
                          )
                        }
                        onBlur={(e) => patch(r.id, { window_hours: Number(e.target.value) })}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        aria-label={`${r.label} cooldown hours`}
                        className={input}
                        type="number"
                        min={0}
                        value={r.cooldown_hours}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((x) =>
                              x.id === r.id ? { ...x, cooldown_hours: Number(e.target.value) } : x,
                            ),
                          )
                        }
                        onBlur={(e) => patch(r.id, { cooldown_hours: Number(e.target.value) })}
                      />
                      <label className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground whitespace-nowrap">
                        escalate after
                        <input
                          aria-label={`${r.label} escalate after repeats`}
                          className={`${input} w-14`}
                          type="number"
                          min={1}
                          value={r.escalate_after_repeats ?? 2}
                          onChange={(e) =>
                            setRules((prev) =>
                              prev.map((x) =>
                                x.id === r.id ? { ...x, escalate_after_repeats: Number(e.target.value) } : x,
                              ),
                            )
                          }
                          onBlur={(e) => patch(r.id, { escalate_after_repeats: Math.max(1, Number(e.target.value)) })}
                        />
                        repeats
                      </label>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.last_conversion_pct == null ? (
                        <span className="text-muted-foreground">not enough data</span>
                      ) : (
                        <span
                          className={`inline-flex items-center gap-1 ${
                            breached ? "text-destructive font-semibold" : ""
                          }`}
                        >
                          {breached && <BellRing className="h-3.5 w-3.5" aria-hidden="true" />}
                          {r.last_conversion_pct}%
                        </span>
                      )}
                      {r.last_alerted_at && (
                        <span className="block text-xs text-muted-foreground">
                          alerted {new Date(r.last_alerted_at).toLocaleString()}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        aria-label={`Enable ${r.label} alerts`}
                        type="checkbox"
                        checked={r.enabled}
                        onChange={(e) => patch(r.id, { enabled: e.target.checked })}
                      />
                    </td>
                  </tr>
                  <tr className="bg-muted/20 text-xs">
                    <td colSpan={7} className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                        <label className="flex items-center gap-2">
                          <span className="uppercase tracking-wider text-muted-foreground">Detect</span>
                          <select
                            aria-label={`${r.label} detection mode`}
                            className="border border-border bg-background px-2 py-1"
                            value={r.detection_mode ?? "threshold"}
                            onChange={(e) => patch(r.id, { detection_mode: e.target.value as Rule["detection_mode"] })}
                          >
                            <option value="threshold">Fixed threshold only</option>
                            <option value="anomaly">Significant drop only</option>
                            <option value="either">Either (threshold or drop)</option>
                            <option value="both">Both must be true</option>
                          </select>
                        </label>
                        <fieldset
                          disabled={(r.detection_mode ?? "threshold") === "threshold"}
                          className="flex flex-wrap items-center gap-x-5 gap-y-2 disabled:opacity-40"
                        >
                          <label className="flex items-center gap-2">
                            <span className="text-muted-foreground">Confidence</span>
                            <select
                              aria-label={`${r.label} anomaly confidence`}
                              className="border border-border bg-background px-2 py-1"
                              value={String(r.anomaly_confidence ?? 95)}
                              onChange={(e) => patch(r.id, { anomaly_confidence: Number(e.target.value) })}
                            >
                              {["90", "95", "99", "99.9"].map((c) => <option key={c} value={c}>{c}%</option>)}
                            </select>
                          </label>
                          <label className="flex items-center gap-2">
                            <span className="text-muted-foreground">Compare to previous</span>
                            <input
                              aria-label={`${r.label} baseline windows`}
                              className={`${input} w-14`}
                              type="number" min={1} max={12}
                              value={r.baseline_windows ?? 4}
                              onChange={(e) => setRules((prev) => prev.map((x) => x.id === r.id ? { ...x, baseline_windows: Number(e.target.value) } : x))}
                              onBlur={(e) => patch(r.id, { baseline_windows: Math.min(12, Math.max(1, Number(e.target.value))) })}
                            />
                            <span className="text-muted-foreground">windows</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <span className="text-muted-foreground">Min drop</span>
                            <input
                              aria-label={`${r.label} minimum relative drop percent`}
                              className={`${input} w-16`}
                              type="number" min={0} max={100}
                              value={r.min_relative_drop_pct ?? 20}
                              onChange={(e) => setRules((prev) => prev.map((x) => x.id === r.id ? { ...x, min_relative_drop_pct: Number(e.target.value) } : x))}
                              onBlur={(e) => patch(r.id, { min_relative_drop_pct: Math.min(100, Math.max(0, Number(e.target.value))) })}
                            />
                            <span className="text-muted-foreground">% vs baseline</span>
                          </label>
                        </fieldset>
                      </div>
                    </td>
                  </tr>
                  </Fragment>
                );
              })}
              {rules.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                    No alert rules configured.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default FunnelAlertRules;
